import type { ReactNode } from "react";
import logoLight from "assets/img/layout/easyfollow-logo.png";
import { MdAdminPanelSettings, MdSchool } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import { getAuthPortalCopy, type AuthPortal } from "lib/authPortalCopy";

type Props = {
  portal: AuthPortal;
  backgroundOnly?: boolean;
};

const portalThemes: Record<
  AuthPortal,
  { gradient: string; icon: ReactNode; accent: string }
> = {
  admin: {
    gradient: "from-brand-600 via-indigo-700 to-navy-900",
    icon: <MdAdminPanelSettings className="h-7 w-7" />,
    accent: "bg-brand-400/20 text-white",
  },
  instructor: {
    gradient: "from-teal-600 via-emerald-700 to-navy-900",
    icon: <MdSchool className="h-7 w-7" />,
    accent: "bg-teal-400/20 text-white",
  },
};

export default function AuthPortalHero({ portal, backgroundOnly = false }: Props) {
  const language = useAppLanguage();
  const t = getAuthPortalCopy(portal, language);
  const theme = portalThemes[portal];

  return (
    <div className={`overflow-hidden ${backgroundOnly ? "absolute inset-0 block min-h-screen w-full" : "relative hidden min-h-screen flex-1 md:block lg:w-[46%]"}`}>
      <div aria-hidden className={`absolute inset-0 bg-gradient-to-br ${theme.gradient}`} />
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.2),transparent_35%),radial-gradient(circle_at_80%_30%,rgba(255,255,255,0.12),transparent_40%)]"
      />

      {!backgroundOnly && <div className="absolute inset-0 flex items-center justify-center p-10">
        <div className="w-full max-w-[520px] rounded-3xl border border-white/15 bg-white/10 p-10 text-white shadow-2xl backdrop-blur-md">
          <div className="flex items-start gap-4">
            <div className={`rounded-2xl p-3 ${theme.accent}`}>{theme.icon}</div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
                {t.portalLabel}
              </p>
              <h2 className="mt-1 text-2xl font-bold leading-snug">{t.heroTitle}</h2>
            </div>
          </div>

          <p className="mt-5 text-sm leading-relaxed text-white/85">{t.heroSubtitle}</p>

          <ul className="mt-8 space-y-3">
            {t.heroBullets.map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm text-white/90">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
                {item}
              </li>
            ))}
          </ul>

          <div className="mt-10 flex items-center justify-between border-t border-white/15 pt-6 text-xs text-white/70">
            <div className="flex items-center gap-2">
              <img src={logoLight} alt="Easyfollow Platform" className="h-5 w-auto opacity-90" />
              <span>Easyfollow Platform</span>
            </div>
            <span>{language === "ar" ? "وصول آمن" : "Secure access"}</span>
          </div>
        </div>
      </div>}
    </div>
  );
}
